import React, { useState, useEffect } from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Users,
  TrendingUp,
  Settings,
  MessageSquare,
  MapPin,
  BarChart3,
  Shield,
  BookOpen,
  Brain,
  PieChart,
  Target,
  Search,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSectionChange: (section: string) => void;
  isSpanish: boolean;
}

interface CommandItem {
  id: string;
  label: string;
  icon: React.ElementType;
  keywords: string[];
  action: () => void;
  group: string;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onSectionChange,
  isSpanish,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onClose]);

  const navigationCommands: CommandItem[] = [
    {
      id: 'summary',
      label: isSpanish ? 'Resumen' : 'Summary',
      icon: BarChart3,
      keywords: ['overview', 'dashboard', 'stats', 'resumen', 'estadísticas'],
      action: () => {
        onSectionChange('summary');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'analytics',
      label: isSpanish ? 'Analíticas' : 'Analytics',
      icon: PieChart,
      keywords: ['analytics', 'charts', 'data', 'analíticas', 'gráficos'],
      action: () => {
        onSectionChange('analytics');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'users',
      label: isSpanish ? 'Usuarios' : 'Users',
      icon: Users,
      keywords: ['users', 'members', 'people', 'usuarios', 'miembros'],
      action: () => {
        onSectionChange('users');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'reports',
      label: isSpanish ? 'Reportes de Frontera' : 'Border Reports',
      icon: MapPin,
      keywords: ['reports', 'border', 'crossing', 'reportes', 'frontera'],
      action: () => {
        onSectionChange('reports');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'points',
      label: isSpanish ? 'Puntos de Recompensa' : 'Reward Points',
      icon: TrendingUp,
      keywords: ['points', 'rewards', 'puntos', 'recompensas'],
      action: () => {
        onSectionChange('points');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'advertisers',
      label: isSpanish ? 'Anunciantes' : 'Advertisers',
      icon: TrendingUp,
      keywords: ['advertisers', 'ads', 'sponsors', 'anunciantes', 'patrocinadores'],
      action: () => {
        onSectionChange('advertisers');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'promos',
      label: isSpanish ? 'Gestión de Promos' : 'Promos Management',
      icon: Target,
      keywords: ['promos', 'promotions', 'offers', 'promociones', 'ofertas'],
      action: () => {
        onSectionChange('promos-management');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'settings',
      label: isSpanish ? 'Configuración' : 'Settings',
      icon: Settings,
      keywords: ['settings', 'preferences', 'config', 'configuración'],
      action: () => {
        onSectionChange('settings');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
    {
      id: 'security',
      label: isSpanish ? 'Seguridad' : 'Security',
      icon: Shield,
      keywords: ['security', 'safety', 'protection', 'seguridad'],
      action: () => {
        onSectionChange('security');
        onClose();
      },
      group: isSpanish ? 'Navegación' : 'Navigation',
    },
  ];

  const actionCommands: CommandItem[] = [
    {
      id: 'search-users',
      label: isSpanish ? 'Buscar usuarios' : 'Search users',
      icon: Search,
      keywords: ['search', 'find', 'buscar', 'usuarios'],
      action: () => {
        onSectionChange('users');
        onClose();
      },
      group: isSpanish ? 'Acciones' : 'Actions',
    },
    {
      id: 'logout',
      label: isSpanish ? 'Cerrar sesión' : 'Logout',
      icon: LogOut,
      keywords: ['logout', 'sign out', 'exit', 'salir', 'cerrar sesión'],
      action: () => {
        navigate('/admin/login');
        onClose();
      },
      group: isSpanish ? 'Acciones' : 'Actions',
    },
  ];

  const allCommands = [...navigationCommands, ...actionCommands];

  const filteredCommands = search
    ? allCommands.filter((cmd) => {
        const searchLower = search.toLowerCase();
        return (
          cmd.label.toLowerCase().includes(searchLower) ||
          cmd.keywords.some((keyword) => keyword.toLowerCase().includes(searchLower))
        );
      })
    : allCommands;

  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.group]) {
      acc[cmd.group] = [];
    }
    acc[cmd.group].push(cmd);
    return acc;
  }, {} as Record<string, CommandItem[]>);

  return (
    <CommandDialog open={isOpen} onOpenChange={onClose}>
      <CommandInput
        placeholder={isSpanish ? 'Buscar comandos...' : 'Search commands...'}
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          {isSpanish ? 'No se encontraron resultados.' : 'No results found.'}
        </CommandEmpty>
        {Object.entries(groupedCommands).map(([group, commands], index) => (
          <React.Fragment key={group}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {commands.map((command) => {
                const Icon = command.icon;
                return (
                  <CommandItem
                    key={command.id}
                    value={command.id}
                    onSelect={command.action}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Icon className="w-4 h-4" />
                    <span>{command.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
};
